import React, {useEffect, useRef, useState} from 'react';
import {Check, Copy} from 'lucide-react';

interface CopyableIdProps {
  /** The value to display and copy */
  value: string;
  /** Optional label rendered before the value, e.g. "ID" */
  label?: string;
  /** Tooltip shown on the copy button */
  title?: string;
  className?: string;
}

/**
 * Writes to the clipboard, falling back to a hidden textarea when the
 * async Clipboard API is unavailable (non-secure contexts, older browsers).
 */
const writeToClipboard = async (value: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Fall through to the legacy path below.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    const copied = document.execCommand('copy');
    if (!copied) throw new Error('Copy command was rejected');
  } finally {
    document.body.removeChild(textarea);
  }
};

export const CopyableId: React.FC<CopyableIdProps> = ({value, label, title = 'Copy to clipboard', className = ''}) => {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopy = async () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    try {
      await writeToClipboard(value);
      setCopied(true);
      setFailed(false);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setCopied(false);
      setFailed(true);
    }
    timeoutRef.current = setTimeout(() => {
      setCopied(false);
      setFailed(false);
    }, 2000);
  };

  return (
    <div className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}>
      {label && <span className="text-xs text-gray-500 dark:text-gray-500 flex-shrink-0">{label}</span>}
      <span className="font-mono text-xs text-gray-600 dark:text-gray-400 truncate" title={value}>
        {value}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        title={failed ? 'Could not copy' : copied ? 'Copied!' : title}
        aria-label={failed ? 'Could not copy' : copied ? 'Copied' : title}
        className="p-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
      >
        {copied ? <Check size={14} className="text-green-600 dark:text-green-400" /> : <Copy size={14} />}
      </button>
      {failed && <span className="text-xs text-red-600 dark:text-red-400">Copy failed</span>}
    </div>
  );
};
